export const communicationTheoryCourse = {
  id: "communication-theory-2024",
  title: "Introduction to Communication Theory: Foundations and Applications",
  shortDescription: "Master fundamental communication theories, models, and concepts that govern human interaction across personal, professional, and mediated contexts.",
  description: "Comprehensive course covering classical and contemporary communication theories, from Shannon-Weaver to Digital Age models, with practical applications in interpersonal, organizational, and mass communication settings.",
  category: "Communication",
  industryCategory: "Communications & Media",
  difficulty: "beginner",
  duration: 15,
  estimatedWeeks: 15,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from communication scholars and industry professionals",
  thumbnailUrl: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Understand fundamental communication theories and their historical development",
    "Analyze communication models from linear to transactional approaches",
    "Apply rhetorical theories to persuasive communication contexts",
    "Evaluate interpersonal communication theories in relationship dynamics",
    "Examine organizational communication theories and workplace applications",
    "Assess mass communication theories in digital media contexts",
    "Develop critical thinking skills for analyzing communication phenomena",
    "Create communication strategies based on theoretical frameworks"
  ],

  prerequisites: [
    "High School Diploma or Equivalent",
    "Basic Writing Skills",
    "Interest in Human Communication",
    "Critical Thinking Abilities"
  ],

  skills: [
    "Communication Theory Analysis",
    "Rhetorical Analysis",
    "Interpersonal Communication",
    "Public Speaking",
    "Media Literacy",
    "Research Methods",
    "Critical Thinking",
    "Writing and Presentation"
  ],

  tags: [
    "Communication Theory",
    "Rhetoric",
    "Interpersonal Communication",
    "Mass Communication",
    "Organizational Communication",
    "Media Studies",
    "Public Speaking",
    "Human Communication"
  ],

  marketData: {
    averageSalary: 65000,
    salaryRange: {
      min: 45000,
      max: 95000
    },
    jobDemand: 4.2,
    marketTrend: "growing",
    careerPaths: [
      "Communications Specialist",
      "Public Relations Manager",
      "Content Writer",
      "Social Media Manager",
      "Corporate Trainer",
      "Marketing Communications"
    ],
    relatedSkills: [
      "Digital Marketing",
      "Content Creation",
      "Public Relations",
      "Social Media Management",
      "Corporate Communications",
      "Training and Development"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "foundations-of-communication",
      title: "Foundations of Communication Theory",
      description: "Historical development and basic principles of communication study",
      order: 1,
      estimatedHours: 6.0,
      content: [
        {
          id: "history-of-communication",
          title: "History and Development of Communication Study",
          description: "Evolution of communication as an academic discipline",
          type: "video",
          duration: 45
        },
        {
          id: "communication-theory-interactive",
          title: "Communication Models Analysis",
          description: "Interactive exploration of linear, interactive, and transactional communication models",
          type: "communication-interactive",
          duration: 60,
          interactiveData: {
            exerciseType: "media-analysis",
            instructions: "Analyze different communication models and their real-world applications. Compare linear, interactive, and transactional models by examining how they explain communication in various contexts.",
            scenario: {
              mediaContent: "You will examine three different communication scenarios: a company memo (linear), a customer service interaction (interactive), and a team brainstorming session (transactional). For each scenario, identify which communication model best explains the process and analyze the strengths and limitations of each model."
            },
            hints: [
              "Consider the direction of communication flow in each model",
              "Think about feedback mechanisms and their importance",
              "Analyze how context and noise affect communication",
              "Evaluate which model best explains modern digital communication"
            ]
          }
        },
        {
          id: "communication-elements",
          title: "Elements of the Communication Process",
          description: "Sender, receiver, message, channel, feedback, and context in communication",
          type: "text",
          duration: 50,
          textContent: `# Elements of the Communication Process

## The Communication Process Model

### Core Components

**Sender (Encoder):**
- **Message Originator**: Person or entity initiating communication
- **Encoding Process**: Converting ideas into symbols, words, or signals
- **Intent and Purpose**: Desired outcome or response from communication
- **Cultural Background**: Influences message formation and delivery style
- **Credibility Factors**: Expertise, trustworthiness, and perceived competence

**Message:**
- **Content**: Information, ideas, emotions being communicated
- **Structure**: Organization and logical flow of information
- **Symbols and Codes**: Language, nonverbal cues, visual elements
- **Primary vs. Secondary Messages**: Explicit content vs. implied meanings
- **Message Clarity**: Precision and understandability of communication

**Channel (Medium):**
- **Verbal Channels**: Face-to-face speech, telephone, video calls
- **Written Channels**: Letters, emails, text messages, documents
- **Nonverbal Channels**: Body language, facial expressions, gestures
- **Digital Channels**: Social media, websites, apps, streaming platforms
- **Channel Characteristics**: Richness, speed, permanence, reach

**Receiver (Decoder):**
- **Message Reception**: Receiving and perceiving the communication
- **Decoding Process**: Interpreting symbols and extracting meaning
- **Frame of Reference**: Personal experiences affecting interpretation
- **Listening/Reading Skills**: Ability to process information effectively
- **Selective Attention**: Filtering and focusing on specific aspects

**Feedback:**
- **Response Types**: Verbal, nonverbal, written, behavioral responses
- **Immediate vs. Delayed**: Real-time reactions vs. later responses
- **Intentional vs. Unintentional**: Conscious feedback vs. unconscious cues
- **Positive, Negative, Neutral**: Different types of feedback responses
- **Feedback Loops**: Continuous exchange of responses

### Environmental Factors

**Context:**
- **Physical Context**: Location, setting, environmental conditions
- **Psychological Context**: Emotional states, attitudes, motivations
- **Social Context**: Relationships, roles, cultural norms
- **Temporal Context**: Timing, historical period, situational urgency
- **Cultural Context**: Shared values, beliefs, communication patterns

**Noise and Interference:**
- **Physical Noise**: Environmental sounds, technical disruptions
- **Psychological Noise**: Preoccupations, emotions, biases
- **Semantic Noise**: Language barriers, unclear terminology
- **Cultural Noise**: Different cultural interpretations and norms
- **Information Overload**: Too much information reducing clarity

## Communication Models Evolution

### Linear Model (Shannon-Weaver)

**Characteristics:**
- **One-Way Process**: Information flows from sender to receiver
- **Mathematical Foundation**: Based on telecommunications engineering
- **Focus on Transmission**: Emphasis on efficient message delivery
- **Noise Consideration**: Recognition of interference factors
- **Limited Interaction**: No provision for feedback or response

**Applications:**
- **Mass Media**: Television, radio, newspaper communications
- **Public Announcements**: Emergency broadcasts, public notices
- **Written Documents**: Policies, procedures, formal reports
- **Technical Communications**: Instructions, specifications, manuals

**Limitations:**
- **No Feedback Loop**: Cannot account for receiver responses
- **Oversimplified Process**: Ignores complexity of human communication
- **Passive Receiver**: Treats receiver as inactive message recipient
- **Context Neglect**: Limited consideration of situational factors

### Interactive Model (Schramm)

**Enhancements:**
- **Feedback Integration**: Includes receiver response to sender
- **Shared Fields**: Recognition of common experience and understanding
- **Circular Process**: Communication as ongoing exchange
- **Active Participants**: Both sender and receiver actively engage
- **Context Awareness**: Greater attention to communication environment

**Key Concepts:**
- **Field of Experience**: Overlapping knowledge and background
- **Role Switching**: Participants alternate between sending and receiving
- **Interpretation**: Meaning-making based on shared understanding
- **Simultaneous Process**: Encoding and decoding occur concurrently

### Transactional Model (Barnlund)

**Advanced Features:**
- **Simultaneous Exchange**: All participants send and receive continuously
- **Co-created Meaning**: Meaning emerges through interaction
- **Multiple Messages**: Various verbal and nonverbal communications
- **Dynamic Process**: Constantly changing and evolving communication
- **Relationship Focus**: Emphasis on interpersonal connections

**Characteristics:**
- **Mutual Influence**: All participants affect the communication outcome
- **Irreversible Process**: Communication cannot be undone or repeated exactly
- **Unrepeatable Events**: Each communication instance is unique
- **Relationship Development**: Communication shapes ongoing relationships
- **Cultural Negotiation**: Meaning negotiated through cultural lenses

## Practical Applications

### Professional Communication

**Business Meetings:**
- **Agenda Setting**: Clear message structure and objectives
- **Participant Roles**: Understanding sender-receiver dynamics
- **Feedback Mechanisms**: Ensuring understanding and engagement
- **Channel Selection**: Choosing appropriate meeting format
- **Context Management**: Creating conducive communication environment

**Email Communication:**
- **Clear Encoding**: Writing clear, purposeful messages
- **Channel Limitations**: Understanding written communication constraints
- **Feedback Delays**: Managing asynchronous communication expectations
- **Noise Reduction**: Minimizing misinterpretation risks
- **Context Provision**: Including necessary background information

### Interpersonal Communication

**Relationship Building:**
- **Shared Experience**: Developing common fields of reference
- **Active Listening**: Effective receiving and feedback skills
- **Nonverbal Awareness**: Reading and sending appropriate cues
- **Context Sensitivity**: Adapting communication to situations
- **Meaning Negotiation**: Working together to create understanding

**Conflict Resolution:**
- **Clear Message Formation**: Expressing concerns and needs clearly
- **Active Feedback**: Seeking and providing constructive responses
- **Noise Management**: Reducing emotional and psychological barriers
- **Channel Appropriateness**: Choosing face-to-face for sensitive topics
- **Context Creation**: Establishing safe communication environments

### Digital Communication

**Social Media:**
- **Audience Awareness**: Understanding diverse receiver characteristics
- **Channel Affordances**: Leveraging platform-specific features
- **Feedback Mechanisms**: Using likes, comments, shares as feedback
- **Context Collapse**: Managing communication across different contexts
- **Noise Management**: Dealing with information overload and distractions

**Online Learning:**
- **Clear Instruction Design**: Effective message encoding for learning
- **Multiple Channels**: Using text, video, audio for redundancy
- **Feedback Systems**: Creating opportunities for learner response
- **Context Support**: Providing adequate learning environment
- **Engagement Strategies**: Maintaining attention and participation

Understanding these communication elements provides the foundation for analyzing and improving all forms of human communication, from intimate conversations to mass media campaigns.`
        }
      ],
      quiz: {
        id: "foundations-quiz",
        title: "Foundations of Communication Theory Quiz",
        description: "Test your understanding of communication theory foundations",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the main difference between the linear and transactional models of communication?",
            options: [
              "Linear model includes noise, transactional does not",
              "Linear model is one-way, transactional involves simultaneous exchange",
              "Linear model is newer than transactional",
              "Linear model is only for digital communication"
            ],
            correctAnswer: 1,
            explanation: "The linear model depicts communication as one-way from sender to receiver, while the transactional model shows communication as a simultaneous, mutual exchange where all participants are both senders and receivers."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In the communication process, 'noise' refers to:",
            options: [
              "Only loud sounds that interfere with hearing",
              "Any interference that affects message transmission or reception",
              "Feedback from the receiver",
              "The communication channel being used"
            ],
            correctAnswer: 1,
            explanation: "Noise in communication theory refers to any form of interference that can disrupt message transmission or reception, including physical, psychological, semantic, and cultural barriers."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "What does 'encoding' mean in the communication process?",
            options: [
              "Translating ideas into symbols or words",
              "Receiving a message",
              "Providing feedback",
              "Choosing a communication channel"
            ],
            correctAnswer: 0,
            explanation: "Encoding is the process by which the sender translates thoughts, ideas, or feelings into symbols, words, or signals that can be transmitted to the receiver."
          }
        ]
      }
    },
    {
      id: "rhetorical-theory",
      title: "Classical and Contemporary Rhetorical Theory",
      description: "Aristotelian rhetoric and modern persuasion theories",
      order: 2,
      estimatedHours: 5.5,
      content: [
        {
          id: "aristotelian-rhetoric",
          title: "Aristotelian Rhetoric: Ethos, Pathos, and Logos",
          description: "Classical foundations of persuasive communication",
          type: "video",
          duration: 40
        },
        {
          id: "persuasion-interactive",
          title: "Persuasive Message Design Workshop",
          description: "Create persuasive messages using rhetorical principles and modern persuasion theories",
          type: "communication-interactive",
          duration: 55,
          interactiveData: {
            exerciseType: "written-communication",
            instructions: "Design a persuasive communication campaign that incorporates classical rhetorical appeals (ethos, pathos, logos) and contemporary persuasion theories. Choose a social issue and create messaging for different audiences.",
            scenario: {
              writingContext: "You are developing a communication campaign to promote sustainable transportation in your community. Your target audiences include college students, working professionals, and seniors. Create persuasive messages that appeal to each group using appropriate rhetorical strategies."
            },
            hints: [
              "Consider how ethos, pathos, and logos work differently with each audience",
              "Use demographic and psychographic factors to tailor your appeals",
              "Apply elaboration likelihood model principles for different involvement levels",
              "Include counterargument strategies and social proof elements"
            ]
          }
        },
        {
          id: "modern-persuasion",
          title: "Contemporary Persuasion Theories",
          description: "Elaboration Likelihood Model, Social Cognitive Theory, and other modern approaches",
          type: "text",
          duration: 45,
          textContent: `# Contemporary Persuasion Theories

## Elaboration Likelihood Model (ELM)

### Core Principles

**Dual-Route Processing:**
- **Central Route**: Careful, thoughtful consideration of message arguments
- **Peripheral Route**: Quick judgments based on simple cues or heuristics
- **Route Determination**: Depends on motivation and ability to process
- **Attitude Change**: Central route produces more durable attitude change
- **Cognitive Resources**: Central processing requires more mental effort

**Factors Affecting Processing Route:**

**Motivation Factors:**
- **Personal Relevance**: How much the issue matters to the individual
- **Need for Cognition**: Individual differences in thinking preferences
- **Accountability**: Whether one must justify decisions to others
- **Involvement Level**: High involvement leads to central processing
- **Responsibility**: Feeling responsible for decision outcomes

**Ability Factors:**
- **Prior Knowledge**: Existing understanding of the topic
- **Cognitive Resources**: Mental capacity available for processing
- **Distraction Levels**: Environmental factors affecting attention
- **Time Constraints**: Pressure to make quick decisions
- **Message Complexity**: Difficulty level of information presented

### Practical Applications

**Marketing Communications:**
- **High-Involvement Products**: Cars, homes requiring central route appeals
- **Low-Involvement Products**: Household items using peripheral cues
- **Celebrity Endorsements**: Peripheral route processing for credibility
- **Technical Specifications**: Central route for knowledgeable consumers
- **Emotional Appeals**: Peripheral route for immediate responses

**Political Communication:**
- **Policy Details**: Central route for engaged, informed voters
- **Campaign Imagery**: Peripheral route for less involved citizens
- **Debate Preparation**: Anticipating both processing routes
- **Voter Education**: Encouraging central route processing
- **Attack Ads**: Often targeting peripheral route responses

## Social Cognitive Theory

### Key Components

**Reciprocal Determinism:**
- **Personal Factors**: Cognitive abilities, emotions, biological factors
- **Environmental Factors**: Social norms, cultural context, resources
- **Behavioral Factors**: Actions, responses, performance outcomes
- **Mutual Influence**: All three factors interact and influence each other
- **Dynamic Process**: Continuous interaction and adaptation

**Self-Efficacy:**
- **Confidence Beliefs**: Beliefs about one's ability to perform behaviors
- **Performance Outcomes**: Past experiences affecting future confidence
- **Vicarious Learning**: Learning from observing others' experiences
- **Verbal Persuasion**: Encouragement and discouragement from others
- **Emotional States**: How feelings affect confidence and performance

**Observational Learning:**
- **Attention**: Focusing on modeled behaviors and their consequences
- **Retention**: Remembering observed behaviors and outcomes
- **Reproduction**: Ability to perform the observed behavior
- **Motivation**: Incentives to engage in the observed behavior
- **Social Modeling**: Learning appropriate behaviors from others

### Communication Applications

**Health Communication:**
- **Role Modeling**: Showing successful health behavior changes
- **Self-Efficacy Building**: Helping people believe they can change
- **Environmental Support**: Creating supportive contexts for change
- **Skill Development**: Teaching necessary abilities for health behaviors
- **Social Influence**: Leveraging peer and family support systems

**Educational Communication:**
- **Modeling Excellence**: Demonstrating desired learning behaviors
- **Confidence Building**: Helping students believe in their abilities
- **Environmental Design**: Creating supportive learning environments
- **Skill Scaffolding**: Building abilities gradually over time
- **Peer Learning**: Using social learning mechanisms

## Theory of Planned Behavior

### Core Elements

**Behavioral Intentions:**
- **Prediction Power**: Intentions strongly predict actual behavior
- **Intention Formation**: Based on three key belief components
- **Specificity**: More specific intentions lead to better prediction
- **Temporal Stability**: Recent intentions predict better than old ones
- **Implementation**: Plans help translate intentions into actions

**Attitude Toward Behavior:**
- **Behavioral Beliefs**: Expected outcomes of performing behavior
- **Outcome Evaluations**: How good or bad outcomes are perceived
- **Expectancy-Value**: Combination of belief strength and evaluation
- **Affective Component**: Emotional reactions to behavior performance
- **Cognitive Component**: Rational assessment of behavior consequences

**Subjective Norms:**
- **Normative Beliefs**: Perceptions of what important others think
- **Motivation to Comply**: Desire to meet others' expectations
- **Social Pressure**: Perceived expectations from significant others
- **Reference Groups**: Influence of different social groups
- **Cultural Context**: How cultural norms shape expectations

**Perceived Behavioral Control:**
- **Control Beliefs**: Perceptions of factors that help or hinder behavior
- **Self-Efficacy**: Confidence in ability to perform behavior
- **Controllability**: Extent to which behavior is under volitional control
- **Resource Availability**: Access to necessary resources and opportunities
- **External Barriers**: Environmental factors affecting behavior performance

### Communication Strategy Development

**Message Design:**
- **Attitude Change**: Addressing behavioral beliefs and outcome evaluations
- **Norm Change**: Influencing perceptions of social expectations
- **Control Enhancement**: Building confidence and addressing barriers
- **Integrated Approach**: Addressing all three components simultaneously
- **Audience Segmentation**: Tailoring messages to different belief profiles

**Formative Research:**
- **Belief Elicitation**: Identifying salient beliefs for target audience
- **Norm Assessment**: Understanding social influence patterns
- **Barrier Identification**: Discovering obstacles to behavior performance
- **Message Testing**: Evaluating communication effectiveness
- **Behavior Tracking**: Monitoring intention-behavior relationships

## Cognitive Dissonance Theory

### Theoretical Foundation

**Dissonance Creation:**
- **Inconsistent Cognitions**: Conflicting beliefs, attitudes, or behaviors
- **Psychological Discomfort**: Unpleasant state motivating change
- **Magnitude Factors**: Importance and number of dissonant elements
- **Ratio of Dissonance**: Dissonant vs. consonant cognitions
- **Individual Differences**: Tolerance for inconsistency varies

**Dissonance Reduction Strategies:**
- **Attitude Change**: Modifying beliefs to match behavior
- **Behavior Change**: Altering actions to match attitudes
- **Adding Cognitions**: Including new supporting information
- **Reducing Importance**: Minimizing significance of inconsistency
- **Selective Exposure**: Seeking confirming information

### Communication Applications

**Commitment and Consistency:**
- **Public Commitments**: Using social pressure to maintain consistency
- **Written Commitments**: Increasing psychological investment
- **Small Steps**: Building commitment through incremental changes
- **Identity Consistency**: Aligning behavior with self-concept
- **Cognitive Realignment**: Helping resolve conflicting beliefs

**Counter-Attitudinal Advocacy:**
- **Role Playing**: Having people argue against their positions
- **Minimal Justification**: Using small incentives for behavior change
- **Choice Emphasis**: Highlighting voluntary nature of decisions
- **Forewarning Effects**: Preparing for dissonance-inducing information
- **Hypocrisy Induction**: Highlighting inconsistencies between values and actions

## Social Proof Theory

### Mechanisms

**Descriptive Norms:**
- **What Others Do**: Information about actual behavior of others
- **Prevalence Information**: How common certain behaviors are
- **Reference Group Behavior**: What similar others are doing
- **Environmental Cues**: Physical evidence of others' choices
- **Statistical Information**: Numerical data about behavior patterns

**Injunctive Norms:**
- **What Others Approve**: Social approval and disapproval patterns
- **Moral Standards**: Ethical guidelines from social groups
- **Explicit Messages**: Direct statements about appropriate behavior
- **Implicit Cues**: Subtle signals about social expectations
- **Enforcement Patterns**: How norms are maintained and violated

### Strategic Applications

**Positive Social Proof:**
- **Success Stories**: Highlighting others' positive experiences
- **Popularity Indicators**: Showing how many people engage in behavior
- **Testimonials**: Using credible others' endorsements
- **Peer Examples**: Featuring similar others as models
- **Trend Information**: Indicating growing adoption of behavior

**Avoiding Negative Social Proof:**
- **Problem Framing**: Focusing on desired rather than undesired behavior
- **Minority Emphasis**: Highlighting that problems are minority behaviors
- **Positive Alternatives**: Showing what most people do instead
- **Dynamic Norms**: Indicating positive trends and changes
- **Contextual Specificity**: Using appropriate reference groups

These contemporary theories provide sophisticated frameworks for understanding and designing persuasive communications in various contexts, from health promotion to political campaigns to marketing communications.`
        }
      ],
      quiz: {
        id: "rhetorical-theory-quiz",
        title: "Rhetorical Theory Quiz",
        description: "Test your understanding of classical and contemporary rhetorical theory",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "According to Aristotle, which rhetorical appeal relies on the credibility of the speaker?",
            options: [
              "Pathos",
              "Logos",
              "Ethos",
              "Kairos"
            ],
            correctAnswer: 2,
            explanation: "Ethos refers to the credibility, character, and trustworthiness of the speaker, which influences the audience's willingness to accept the message."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In the Elaboration Likelihood Model, what determines which processing route a person takes?",
            options: [
              "The speaker's credibility only",
              "The message length only",
              "Their motivation and ability to process the message",
              "The communication channel used"
            ],
            correctAnswer: 2,
            explanation: "The ELM suggests that both motivation (personal relevance, need for cognition) and ability (knowledge, cognitive resources, time) determine whether someone processes a message through the central or peripheral route."
          }
        ]
      }
    },
    {
      id: "interpersonal-communication",
      title: "Interpersonal Communication Theory",
      description: "Relationship development, self-disclosure, and social exchange theories",
      order: 3,
      estimatedHours: 5.0,
      content: [
        {
          id: "relationship-development",
          title: "Social Penetration and Relationship Development",
          description: "How relationships form and develop through communication",
          type: "video",
          duration: 35
        },
        {
          id: "interpersonal-skills-practice",
          title: "Interpersonal Communication Skills Lab",
          description: "Practice active listening, empathy, and conflict resolution in realistic scenarios",
          type: "communication-interactive",
          duration: 50,
          interactiveData: {
            exerciseType: "interpersonal-skills",
            instructions: "Analyze challenging interpersonal communication scenarios and develop strategies using communication theories. Focus on active listening, empathy, nonverbal communication, and conflict resolution approaches.",
            scenario: {
              interpersonalScenario: "You are mediating a conflict between two team members who have different communication styles and cultural backgrounds. One prefers direct communication and the other values harmony and indirect approaches. They need to collaborate on an important project but have been experiencing misunderstandings and tension."
            },
            hints: [
              "Apply social penetration theory to understand relationship levels",
              "Use uncertainty reduction theory to address communication apprehension",
              "Consider cultural communication patterns and preferences",
              "Apply conflict resolution strategies that respect both communication styles"
            ]
          }
        },
        {
          id: "self-disclosure-theory",
          title: "Self-Disclosure and Social Exchange in Relationships",
          description: "Theoretical frameworks for understanding relationship communication",
          type: "text",
          duration: 40,
          textContent: `# Self-Disclosure and Social Exchange in Relationships

## Social Penetration Theory

### Core Concepts

**Onion Metaphor:**
- **Surface Level**: Biographical information, preferences, basic facts
- **Intermediate Levels**: Opinions, attitudes, personal experiences
- **Core Level**: Deep values, fears, intimate secrets, core identity
- **Gradual Penetration**: Relationships develop through layers over time
- **Reciprocal Process**: Both parties gradually reveal more personal information

**Dimensions of Self-Disclosure:**

**Breadth:**
- **Topic Range**: Number of different subjects discussed
- **Life Areas**: Work, family, relationships, personal history, beliefs
- **Breadth Development**: Early relationships focus on expanding topics
- **Surface Exploration**: Getting to know various aspects of someone
- **Conversational Variety**: Diverse areas of shared communication

**Depth:**
- **Intimacy Level**: How personal and private information becomes
- **Emotional Significance**: Importance of shared information to self-concept
- **Vulnerability**: Degree of risk involved in sharing information
- **Trust Development**: Deeper disclosure requires and builds trust
- **Relationship Investment**: Deeper sharing indicates commitment

**Frequency:**
- **Disclosure Rate**: How often personal information is shared
- **Communication Patterns**: Regular vs. sporadic intimate conversations
- **Relationship Maintenance**: Ongoing disclosure maintains closeness
- **Balance Issues**: Too much or too little disclosure can create problems
- **Timing Considerations**: Appropriate pacing of disclosure development

### Relationship Development Stages

**Orientation Stage:**
- **Small Talk**: Safe, conventional topics dominate conversation
- **Social Norms**: Following cultural scripts for initial interactions
- **Low Risk**: Minimal vulnerability in information shared
- **Impression Management**: Presenting socially desirable self-image
- **Testing Waters**: Cautious exploration of compatibility

**Exploratory Affective Exchange:**
- **Personal Opinions**: Sharing attitudes and preferences
- **Value Expression**: Revealing beliefs and principles
- **Increased Intimacy**: Moving beyond surface-level information
- **Selective Disclosure**: Choosing what to reveal more carefully
- **Compatibility Assessment**: Evaluating relationship potential

**Affective Exchange:**
- **Emotional Sharing**: Expressing feelings and emotional experiences
- **Personal History**: Revealing significant life events and experiences
- **Vulnerability**: Taking emotional risks through disclosure
- **Mutual Support**: Providing and seeking emotional assistance
- **Deeper Understanding**: Developing comprehensive knowledge of partner

**Stable Exchange:**
- **Open Communication**: Free and honest expression of thoughts and feelings
- **Predictable Patterns**: Established communication rhythms and styles
- **High Intimacy**: Sharing most personal aspects of self
- **Mutual Knowledge**: Extensive understanding of partner's inner world
- **Relationship Security**: Confidence in relationship stability and acceptance

## Self-Disclosure Theory

### Functions of Self-Disclosure

**Relationship Development:**
- **Trust Building**: Sharing personal information demonstrates trust
- **Intimacy Creation**: Mutual disclosure deepens emotional connection
- **Bonding Mechanism**: Shared secrets create special relationship ties
- **Vulnerability Management**: Appropriate risk-taking strengthens relationships
- **Commitment Indication**: Disclosure signals investment in relationship

**Self-Expression:**
- **Identity Confirmation**: Sharing who we are with important others
- **Authenticity**: Being genuine and real in relationships
- **Self-Understanding**: Talking about ourselves helps clarify self-concept
- **Validation Seeking**: Getting acceptance and affirmation from others
- **Emotional Release**: Sharing burdens and expressing emotions

**Social Control:**
- **Impression Management**: Strategic sharing to influence others' perceptions
- **Boundary Setting**: Controlling what others know about us
- **Power Dynamics**: Information sharing can influence relationship power
- **Influence Attempts**: Using personal information to persuade others
- **Relationship Regulation**: Managing closeness through disclosure decisions

### Factors Affecting Self-Disclosure

**Individual Factors:**
- **Personality Traits**: Extroversion, openness, trust, self-esteem
- **Communication Apprehension**: Fear of negative evaluation affects sharing
- **Cultural Background**: Individualistic vs. collectivistic disclosure norms
- **Gender Socialization**: Different expectations for male and female disclosure
- **Past Experiences**: Previous relationship outcomes influence disclosure willingness

**Relational Factors:**
- **Relationship Stage**: Earlier vs. later stages require different disclosure levels
- **Partner Responsiveness**: How partner receives and responds to disclosure
- **Reciprocity Expectations**: Balance of sharing between relationship partners
- **Relationship Quality**: Satisfaction and commitment affect disclosure patterns
- **Power Balance**: Equal vs. unequal relationships influence sharing comfort

**Contextual Factors:**
- **Privacy**: Physical and psychological space for intimate conversation
- **Time Availability**: Sufficient time for meaningful disclosure
- **Cultural Context**: Social norms about appropriate sharing
- **Situational Appropriateness**: Matching disclosure to context requirements
- **Social Support**: Encouragement from others to share and be open

## Social Exchange Theory

### Fundamental Principles

**Cost-Benefit Analysis:**
- **Relationship Rewards**: Benefits gained from relationship participation
- **Relationship Costs**: Efforts, sacrifices, and negative experiences
- **Net Outcomes**: Rewards minus costs equals relationship satisfaction
- **Comparison Processes**: Evaluating current relationship against alternatives
- **Exchange Motivation**: People seek to maximize rewards while minimizing costs

**Types of Resources Exchanged:**

**Emotional Resources:**
- **Love and Affection**: Expressions of care and emotional attachment
- **Emotional Support**: Comfort, empathy, and understanding during difficulties
- **Acceptance**: Unconditional positive regard and validation
- **Security**: Feelings of safety and stability in relationship
- **Companionship**: Enjoyable time spent together and shared experiences

**Instrumental Resources:**
- **Practical Help**: Assistance with tasks, problems, and daily activities
- **Financial Support**: Money, gifts, and material assistance
- **Information**: Advice, knowledge, and useful information sharing
- **Services**: Acts of service and helpful behaviors
- **Time and Attention**: Availability and focused attention when needed

**Social Resources:**
- **Status**: Association with partner enhances social standing
- **Social Connections**: Access to partner's social network and relationships
- **Social Identity**: Being part of a recognized relationship or family unit
- **Public Recognition**: Social acknowledgment of relationship importance
- **Shared Social Activities**: Participation in couple and family events

### Exchange Patterns

**Reciprocity Principles:**
- **Immediate Reciprocity**: Quick return of similar benefits
- **Delayed Reciprocity**: Trust that benefits will be returned over time
- **Approximate Reciprocity**: Similar but not exactly equal exchanges
- **Generalized Reciprocity**: Giving without expectation of specific return
- **Cultural Reciprocity**: Exchange patterns influenced by cultural norms

**Equity Considerations:**
- **Proportional Exchanges**: Benefits proportional to contributions
- **Fairness Perceptions**: Subjective evaluations of exchange fairness
- **Equity Restoration**: Actions taken when exchanges feel unfair
- **Individual Differences**: Varying sensitivity to equity issues
- **Relationship Stage**: Different equity expectations at different stages

## Uncertainty Reduction Theory

### Core Assumptions

**Uncertainty as Discomfort:**
- **Cognitive Discomfort**: Not knowing creates psychological tension
- **Predictability Needs**: Desire to anticipate others' behavior
- **Control Motivation**: Uncertainty reduces sense of control
- **Anxiety Creation**: Unknown outcomes generate worry and stress
- **Information Seeking**: Drive to gather clarifying information

**Uncertainty Types:**

**Behavioral Uncertainty:**
- **Action Prediction**: Not knowing what someone will do
- **Response Patterns**: Uncertainty about others' typical behaviors
- **Decision Making**: Unpredictability in choices and preferences
- **Emotional Reactions**: Not knowing how someone will respond emotionally
- **Communication Styles**: Uncertainty about preferred interaction patterns

**Cognitive Uncertainty:**
- **Attitude Uncertainty**: Not knowing others' beliefs and values
- **Thought Processes**: Uncertainty about how others think and reason
- **Motivation Understanding**: Not knowing why others act as they do
- **Perspective Taking**: Difficulty understanding others' viewpoints
- **Mental Models**: Uncertainty about others' worldviews and frameworks

### Uncertainty Reduction Strategies

**Passive Strategies:**
- **Observation**: Watching others in various situations
- **Environmental Scanning**: Noting others' possessions, spaces, associations
- **Social Information**: Gathering information from third parties
- **Online Research**: Using digital sources to learn about others
- **Indirect Assessment**: Making inferences from available evidence

**Active Strategies:**
- **Direct Questions**: Asking others about themselves and their preferences
- **Environmental Manipulation**: Creating situations to observe reactions
- **Third-Party Inquiry**: Asking mutual friends for information
- **Social Network Analysis**: Understanding others through their relationships
- **Structured Interaction**: Engaging in activities that reveal information

**Interactive Strategies:**
- **Self-Disclosure**: Sharing personal information to encourage reciprocity
- **Direct Communication**: Open discussion about thoughts and feelings
- **Relational Talk**: Conversations about relationship expectations and goals
- **Conflict Engagement**: Working through disagreements to understand perspectives
- **Intimacy Development**: Deepening emotional connection to reduce uncertainty

Understanding these theories provides insight into how relationships develop, how communication creates intimacy, and how people manage the complex exchange of resources and information that characterizes close relationships.`
        }
      ],
      quiz: {
        id: "interpersonal-quiz",
        title: "Interpersonal Communication Theory Quiz",
        description: "Test your understanding of relationship and interpersonal communication theories",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "According to Social Penetration Theory, relationships develop through:",
            options: [
              "Immediate deep disclosure",
              "Gradual movement from surface to intimate topics",
              "Sharing only positive information",
              "Avoiding personal topics"
            ],
            correctAnswer: 1,
            explanation: "Social Penetration Theory suggests that relationships develop gradually as people move from surface-level topics to more intimate and personal disclosure, like peeling back layers of an onion."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In Social Exchange Theory, people are motivated to:",
            options: [
              "Always give more than they receive",
              "Maximize rewards while minimizing costs",
              "Avoid all relationship costs",
              "Exchange only material resources"
            ],
            correctAnswer: 1,
            explanation: "Social Exchange Theory proposes that people are motivated to maximize rewards (benefits) while minimizing costs in their relationships, seeking the most favorable cost-benefit ratio."
          }
        ]
      }
    },
    {
      id: "organizational-communication",
      title: "Organizational Communication Theory",
      description: "Systems theory, organizational culture, and workplace communication",
      order: 4,
      estimatedHours: 4.5,
      content: [
        {
          id: "systems-theory",
          title: "Systems Theory in Organizations",
          description: "Understanding organizations as complex communication systems",
          type: "video",
          duration: 35
        },
        {
          id: "workplace-communication-lab",
          title: "Workplace Communication Scenarios",
          description: "Apply organizational communication theories to workplace challenges",
          type: "communication-interactive",
          duration: 45,
          interactiveData: {
            exerciseType: "written-communication",
            instructions: "Analyze organizational communication challenges and design solutions using systems theory, organizational culture concepts, and formal/informal communication networks. Address communication flow, hierarchy, and culture issues.",
            scenario: {
              writingContext: "You are a communication consultant hired by a mid-size technology company experiencing communication breakdowns between departments, low employee engagement, and confusion about company direction. Design a comprehensive communication strategy that addresses formal and informal communication networks, organizational culture, and information flow issues."
            },
            hints: [
              "Consider both formal and informal communication networks",
              "Apply systems theory to understand interdepartmental relationships",
              "Address upward, downward, and horizontal communication flows",
              "Include strategies for building organizational culture through communication"
            ]
          }
        },
        {
          id: "organizational-culture-communication",
          title: "Organizational Culture and Communication Climate",
          description: "How communication creates and maintains organizational culture",
          type: "text",
          duration: 35,
          textContent: `# Organizational Culture and Communication Climate

## Organizational Culture Theory

### Culture Definition and Components

**Organizational Culture:**
- **Shared Values**: Common beliefs about what is important and worthwhile
- **Basic Assumptions**: Underlying, unconscious beliefs about reality and truth
- **Artifacts**: Visible manifestations of culture (symbols, rituals, language)
- **Behavioral Norms**: Unwritten rules about appropriate behavior
- **Collective Identity**: Shared sense of belonging and organizational character

**Schein's Levels of Culture:**

**Level 1: Artifacts**
- **Physical Environment**: Office layout, building design, workspace organization
- **Dress Codes**: Formal vs. casual, uniformity vs. individual expression
- **Language and Jargon**: Specialized terminology, insider language, acronyms
- **Rituals and Ceremonies**: Regular meetings, celebrations, recognition events
- **Stories and Legends**: Narratives about organizational heroes and important events

**Level 2: Espoused Values**
- **Mission Statements**: Publicly declared organizational purposes and goals
- **Operating Principles**: Stated guidelines for decision making and behavior
- **Strategic Goals**: Articulated objectives and priorities
- **Ethical Standards**: Expressed moral principles and conduct expectations
- **Quality Standards**: Declared commitments to excellence and performance

**Level 3: Basic Assumptions**
- **Human Nature**: Beliefs about people's inherent characteristics and motivations
- **Reality and Truth**: Assumptions about what constitutes valid information
- **Time Orientation**: Past, present, or future focus in planning and decision making
- **Space and Environment**: Beliefs about physical and social environment relationships
- **Activity Orientation**: Preferences for being, thinking, or doing approaches

### Cultural Communication Patterns

**High-Context Cultures:**
- **Implicit Communication**: Meaning embedded in context and relationships
- **Nonverbal Emphasis**: Importance of body language, tone, and setting
- **Relationship Focus**: Priority on maintaining harmony and relationships
- **Indirect Communication**: Subtle, diplomatic expression of ideas and concerns
- **Collective Decision Making**: Group consensus and consultation processes

**Low-Context Cultures:**
- **Explicit Communication**: Direct, clear, specific verbal expression
- **Content Focus**: Emphasis on facts, data, and explicit information
- **Task Orientation**: Priority on efficiency and goal achievement
- **Direct Communication**: Straightforward expression of ideas and feedback
- **Individual Decision Making**: Personal responsibility and autonomy

## Communication Climate

### Climate Characteristics

**Supportive Climate:**
- **Trust and Openness**: Safe environment for honest communication
- **Participation**: Encouragement of employee input and involvement
- **Recognition**: Acknowledgment of contributions and achievements
- **Innovation**: Support for new ideas and creative problem solving
- **Learning Orientation**: Emphasis on growth and development

**Defensive Climate:**
- **Fear and Suspicion**: Reluctance to share information or take risks
- **Control**: Emphasis on hierarchy, rules, and micromanagement
- **Criticism**: Focus on blame and fault-finding
- **Conformity**: Pressure to follow established patterns without question
- **Competition**: Internal rivalry that undermines collaboration

### Factors Influencing Climate

**Leadership Communication:**
- **Communication Style**: Authoritarian vs. participative approaches
- **Accessibility**: Leader availability and approachability
- **Feedback Patterns**: Frequency and quality of performance feedback
- **Transparency**: Openness about organizational decisions and changes
- **Vision Communication**: Clarity and inspiration in direction setting

**Organizational Structure:**
- **Hierarchy Levels**: Number of management layers affecting communication flow
- **Span of Control**: Number of direct reports affecting communication patterns
- **Centralization**: Degree of decision-making concentration
- **Formalization**: Extent of written rules and procedures
- **Departmentalization**: How work is divided and coordinated

**Communication Policies:**
- **Open Door Policies**: Accessibility of management to employee concerns
- **Information Sharing**: Guidelines about what information is shared widely
- **Feedback Systems**: Formal mechanisms for upward communication
- **Conflict Resolution**: Procedures for addressing disagreements and problems
- **Employee Voice**: Opportunities for employee input and participation

## Formal Communication Networks

### Hierarchical Communication

**Downward Communication:**
- **Task Instructions**: Job assignments, procedures, and performance expectations
- **Policy Information**: Rules, regulations, and organizational guidelines
- **Performance Feedback**: Evaluation results and improvement suggestions
- **Goal Communication**: Organizational objectives and strategic direction
- **Socialization**: Information about organizational culture and values

**Upward Communication:**
- **Performance Reports**: Progress updates and achievement summaries
- **Problem Identification**: Issues requiring management attention and resolution
- **Suggestions**: Employee ideas for improvement and innovation
- **Feedback**: Employee responses to policies, procedures, and decisions
- **Grievances**: Formal complaints and concerns about workplace conditions

**Horizontal Communication:**
- **Coordination**: Information sharing between departments and teams
- **Problem Solving**: Collaborative approaches to addressing challenges
- **Conflict Resolution**: Peer-to-peer resolution of disagreements
- **Social Support**: Emotional and professional support among colleagues
- **Innovation**: Cross-functional collaboration on new ideas and projects

### Network Structures

**Chain Network:**
- **Linear Structure**: Information flows in straight line through hierarchy
- **Centralized Control**: Clear authority and information control
- **Slow Information Flow**: Multiple steps required for information transmission
- **Limited Feedback**: Minimal opportunities for upward communication
- **Suitable for**: Simple, routine tasks with clear procedures

**Wheel Network:**
- **Central Hub**: One person controls all information flow
- **Efficient for Simple Tasks**: Quick decision making for routine issues
- **Bottleneck Risk**: Central person can become overwhelmed
- **Limited Collaboration**: Peripheral members don't communicate directly
- **High Dependence**: Network effectiveness depends on central person

**All-Channel Network:**
- **Full Connectivity**: Everyone can communicate with everyone else
- **High Participation**: Maximum opportunity for input and collaboration
- **Complex Coordination**: Difficult to manage information flow and decisions
- **Suitable for Complex Tasks**: Creative and problem-solving activities
- **Democratic Process**: Shared decision making and responsibility

## Informal Communication Networks

### Grapevine Communication

**Characteristics:**
- **Unofficial Channels**: Communication outside formal organizational structure
- **Rapid Transmission**: Often faster than formal communication
- **Selective Information**: Not all information travels through grapevine
- **Personal Relationships**: Based on friendship and social connections
- **Emotional Content**: Often carries feelings and interpretations along with facts

**Grapevine Patterns:**
- **Single Strand**: Information passes from person to person in chain
- **Gossip Chain**: One person tells many others who don't pass it on
- **Probability Chain**: Random sharing with others encountered
- **Cluster Chain**: Selective sharing with chosen group members

**Functions:**
- **Information Gap Filling**: Provides information not available through formal channels
- **Social Bonding**: Strengthens relationships and group cohesion
- **Emotional Expression**: Outlet for feelings about organizational issues
- **Influence Attempts**: Informal persuasion and opinion formation
- **Early Warning System**: Alerts about potential changes and problems

### Managing Informal Networks

**Positive Approaches:**
- **Information Transparency**: Reducing need for grapevine through open communication
- **Network Awareness**: Understanding informal influence patterns
- **Relationship Building**: Developing positive connections with informal leaders
- **Feedback Monitoring**: Listening to informal network messages
- **Culture Building**: Using informal networks to reinforce positive values

**Addressing Problems:**
- **Rumor Management**: Quickly addressing false information
- **Trust Building**: Developing credibility to compete with informal sources
- **Channel Improvement**: Enhancing formal communication effectiveness
- **Climate Assessment**: Understanding why people rely on informal channels
- **Leadership Development**: Training managers to work effectively with informal networks

Understanding organizational culture and communication climate provides insight into how communication shapes organizational life and how leaders can create environments that support effective communication, collaboration, and performance.`
        }
      ],
      quiz: {
        id: "organizational-quiz",
        title: "Organizational Communication Theory Quiz",
        description: "Test your understanding of organizational communication concepts",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "According to Schein's model, organizational artifacts include:",
            options: [
              "Basic underlying assumptions",
              "Espoused values and beliefs",
              "Observable elements like dress codes and physical environment",
              "Management's private opinions"
            ],
            correctAnswer: 2,
            explanation: "In Schein's three-level model of organizational culture, artifacts are the visible, observable elements including physical environment, dress codes, language, rituals, and other surface-level manifestations of culture."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "The 'grapevine' in organizational communication refers to:",
            options: [
              "Formal reporting structures",
              "Informal communication networks",
              "Official company newsletters",
              "Management communication only"
            ],
            correctAnswer: 1,
            explanation: "The grapevine refers to informal communication networks that exist outside the formal organizational structure, often carrying information, rumors, and social messages through personal relationships."
          }
        ]
      }
    },
    {
      id: "mass-communication",
      title: "Mass Communication and Media Theory",
      description: "Media effects, agenda setting, and digital communication theories",
      order: 5,
      estimatedHours: 5.0,
      content: [
        {
          id: "media-effects-theory",
          title: "Media Effects and Agenda Setting Theory",
          description: "How mass media influences public opinion and behavior",
          type: "video",
          duration: 40
        },
        {
          id: "digital-media-analysis",
          title: "Digital Media and Communication Analysis",
          description: "Analyze contemporary digital communication phenomena using media theories",
          type: "communication-interactive",
          duration: 55,
          interactiveData: {
            exerciseType: "digital-storytelling",
            instructions: "Create a digital communication strategy that demonstrates understanding of media effects theories, agenda setting, cultivation theory, and digital age communication patterns. Design content for multiple platforms considering audience fragmentation and media convergence.",
            scenario: {
              storyContext: "You are developing a public awareness campaign about climate change adaptation for a diverse audience across different demographics and media consumption patterns. Your campaign must work across traditional and digital media platforms while considering media effects theories and audience behavior patterns."
            },
            hints: [
              "Apply agenda setting theory to determine message prioritization",
              "Consider cultivation theory effects on audience perceptions",
              "Use uses and gratifications theory to understand audience motivations",
              "Account for digital media characteristics like interactivity and convergence"
            ]
          }
        },
        {
          id: "digital-age-theories",
          title: "Communication Theories in the Digital Age",
          description: "New theoretical frameworks for understanding digital communication",
          type: "text",
          duration: 45,
          textContent: `# Communication Theories in the Digital Age

## Media Ecology Theory

### Theoretical Foundation

**Medium as Environment:**
- **Technological Environment**: Media technologies create environments that shape human behavior
- **Symbiotic Relationship**: Humans and media technologies influence each other
- **Environmental Effects**: Media environments affect how we think, feel, and behave
- **Invisible Influence**: We often don't notice how media shapes our experience
- **Structural Impact**: Media structure affects content and meaning

**McLuhan's Insights:**
- **"Medium is the Message"**: The characteristics of communication technology matter more than content
- **Hot vs. Cool Media**: High-definition (hot) vs. low-definition (cool) media require different participation
- **Global Village**: Electronic media creates worldwide interconnectedness
- **Extensions of Man**: Media technologies extend human capabilities and senses
- **Retrieval and Obsolescence**: New media retrieve old patterns while making others obsolete

### Digital Media Characteristics

**Interactivity:**
- **User Control**: Audiences can control content access, pacing, and sequence
- **Two-Way Communication**: Traditional sender-receiver roles become blurred
- **Participation Culture**: Audiences become content creators and distributors
- **Real-Time Feedback**: Immediate responses and reactions possible
- **Customization**: Content can be personalized to individual preferences

**Convergence:**
- **Media Integration**: Previously separate media combine in digital platforms
- **Content Mobility**: Same content accessible across multiple devices and platforms
- **Industry Convergence**: Media, telecommunications, and computer industries merge
- **Cultural Convergence**: New forms of cultural expression and participation
- **Economic Convergence**: New business models and revenue streams

**Networked Communication:**
- **Many-to-Many**: Everyone can potentially communicate with everyone else
- **Decentralized Structure**: No single point of control over information flow
- **Viral Spreading**: Information can spread rapidly through network connections
- **Weak Tie Strength**: Connections with distant others become communicatively significant
- **Network Effects**: Value increases with number of network participants

## Uses and Gratifications Theory 2.0

### Traditional Uses and Gratifications

**Active Audience Assumption:**
- **Goal-Oriented**: Audiences have specific needs they seek to satisfy
- **Media Choice**: People actively select media to meet their needs
- **Selective Exposure**: Audiences choose content that serves their purposes
- **Individual Differences**: People have different media needs and preferences
- **Social and Psychological Factors**: Personal characteristics influence media use

**Traditional Gratifications:**
- **Information**: Learning about events, issues, and practical matters
- **Entertainment**: Enjoyment, escapism, and emotional release
- **Personal Identity**: Confirmation of values and identity construction
- **Social Integration**: Connection with family, friends, and society
- **Tension Release**: Relaxation and stress reduction

### Digital Age Adaptations

**Enhanced Interactivity Gratifications:**
- **Control**: Ability to control media experience and content selection
- **Participation**: Opportunity to contribute content and engage actively
- **Customization**: Personalizing media environment to individual preferences
- **Real-Time Engagement**: Immediate interaction and response capabilities
- **User-Generated Content**: Creating and sharing original content

**Social Networking Gratifications:**
- **Relationship Maintenance**: Staying connected with existing social networks
- **Relationship Building**: Forming new connections and expanding networks
- **Social Surveillance**: Monitoring others' activities and updates
- **Social Capital**: Building and leveraging social resources and connections
- **Identity Performance**: Presenting and managing online identity

**Mobile Media Gratifications:**
- **Accessibility**: Constant connection and information access
- **Location-Based Services**: Context-sensitive information and services
- **Multitasking**: Simultaneous engagement in multiple activities
- **Time-Filling**: Using brief moments for media consumption
- **Social Coordination**: Real-time coordination of activities with others

## Agenda Setting Theory 2.0

### Traditional Agenda Setting

**First Level - Object Salience:**
- **Issue Attention**: Media coverage affects what issues people think about
- **Prominence**: More coverage leads to higher issue importance ratings
- **Transfer Process**: Media agenda influences public agenda over time
- **Cognitive Effects**: Changes in what people consider important issues
- **Political Implications**: Agenda setting affects political priorities and policies

**Second Level - Attribute Salience:**
- **Framing Effects**: How media covers issues affects how people think about them
- **Substantive Attributes**: Characteristics and qualities emphasized in coverage
- **Affective Attributes**: Emotional tone and evaluative dimensions
- **Image Formation**: How coverage shapes perceptions of people and issues
- **Cognitive Mapping**: Mental organization of issue-related information

### Digital Age Complexities

**Fragmented Agenda Setting:**
- **Audience Fragmentation**: Different groups follow different media sources
- **Echo Chambers**: People expose themselves to confirming information
- **Filter Bubbles**: Algorithmic filtering creates personalized information environments
- **Multiple Agendas**: Various niche agendas rather than single public agenda
- **Polarization**: Divergent agenda priorities across political and social groups

**Algorithmic Agenda Setting:**
- **Platform Algorithms**: Automated systems determine content visibility
- **Personalization**: Individual-level agenda setting through customized feeds
- **Engagement Metrics**: Clicks, shares, and comments influence content promotion
- **Data-Driven Decisions**: User behavior data shapes content recommendations
- **Black Box Problems**: Lack of transparency in algorithmic decision making

**User-Generated Agenda Setting:**
- **Citizen Journalism**: Individuals can set agendas through content creation
- **Viral Content**: User sharing can rapidly elevate issue prominence
- **Hashtag Campaigns**: Organized efforts to focus attention on issues
- **Crowdsourced News**: Collective news gathering and priority setting
- **Bottom-Up Influence**: Audiences can influence traditional media agendas

## Network Society Theory

### Castells' Network Society

**Space of Flows:**
- **Information Space**: Physical location becomes less important than information access
- **Global Connectivity**: Real-time communication across geographic distances
- **Virtual Organizations**: Companies and institutions operate across space electronically
- **Cultural Globalization**: Shared cultural experiences across locations
- **Power Redistribution**: Network access becomes source of power and influence

**Timeless Time:**
- **Instant Communication**: Real-time global communication capabilities
- **24/7 Society**: Continuous operation across time zones
- **Compression of Time**: Faster cycles of production, consumption, and communication
- **Simultaneous Experience**: Shared experiences across different time zones
- **Historical Compression**: Past, present, and future become compressed

**Network Logic:**
- **Inclusion and Exclusion**: Being connected vs. disconnected determines opportunities
- **Flexibility**: Networks can reconfigure rapidly in response to changes
- **Decentralization**: No fixed center or hierarchy in network structure
- **Interdependence**: Individual nodes depend on network connectivity
- **Collective Intelligence**: Networks generate knowledge through connections

### Digital Divide Implications

**Access Divides:**
- **Infrastructure**: Availability of high-speed internet and digital devices
- **Economic**: Ability to afford technology and internet access
- **Geographic**: Rural vs. urban differences in connectivity
- **Age**: Generational differences in technology adoption and skills
- **Education**: Knowledge and skills needed for effective technology use

**Participation Divides:**
- **Content Creation**: Ability to produce and share digital content
- **Political Participation**: Using technology for civic and political engagement
- **Economic Opportunities**: Access to digital economy jobs and markets
- **Social Capital**: Building valuable relationships through digital networks
- **Cultural Expression**: Opportunities for creative and cultural participation

## Affordances Theory

### Technological Affordances

**Definition and Characteristics:**
- **Action Possibilities**: What actions a technology enables or constrains
- **Relational Concept**: Affordances exist in relation between user and technology
- **Contextual Variation**: Same technology may afford different actions in different contexts
- **Perceived vs. Actual**: What users think they can do vs. what they actually can do
- **Cultural Shaping**: Social and cultural factors influence affordance perception

**Communication Technology Affordances:**

**Visibility:**
- **Public Communication**: Making communication visible to larger audiences
- **Transparency**: Ability to see into previously private communications
- **Awareness**: Knowledge of others' activities and communications
- **Documentation**: Creating permanent records of communication
- **Searchability**: Finding and retrieving past communications

**Persistence:**
- **Message Durability**: Communications remain available over time
- **Archival Capability**: Long-term storage and retrieval of communications
- **Version Control**: Tracking changes and evolution of communications
- **Evidence**: Persistent communications serve as evidence and proof
- **Memory Enhancement**: Externalized memory of conversations and interactions

**Editability:**
- **Revision Capability**: Ability to modify communications before sending
- **Improvement**: Editing to enhance clarity and effectiveness
- **Strategic Presentation**: Careful crafting of self-presentation
- **Error Correction**: Fixing mistakes before communication is final
- **Collaborative Editing**: Multiple people working on same communication

**Scalability:**
- **Audience Expansion**: Reaching larger audiences than possible face-to-face
- **Viral Potential**: Communications can spread rapidly to massive audiences
- **Network Effects**: Value increases with number of participants
- **Broadcast Capability**: One-to-many communication possibilities
- **Global Reach**: Overcoming geographic limitations on communication

These digital age theories help us understand how new communication technologies create new possibilities and constraints for human communication, requiring updated theoretical frameworks to explain contemporary communication phenomena.`
        }
      ],
      quiz: {
        id: "mass-communication-quiz",
        title: "Mass Communication and Media Theory Quiz",
        description: "Test your understanding of mass communication and digital media theories",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Agenda Setting Theory suggests that mass media:",
            options: [
              "Tells people what to think",
              "Tells people what to think about",
              "Has no influence on public opinion",
              "Only affects highly educated audiences"
            ],
            correctAnswer: 1,
            explanation: "Agenda Setting Theory proposes that while media may not tell people what to think, it significantly influences what people think about by determining which issues receive attention and coverage."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "According to Uses and Gratifications Theory, media audiences are:",
            options: [
              "Passive recipients of media messages",
              "Active seekers who choose media to meet specific needs",
              "Easily manipulated by media content",
              "Only influenced by media during crises"
            ],
            correctAnswer: 1,
            explanation: "Uses and Gratifications Theory views audiences as active participants who deliberately choose and use media to satisfy specific psychological and social needs, rather than passive recipients of media influence."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "communication-theory-analysis",
      title: "Communication Theory Analysis Project",
      description: "Comprehensive analysis of communication phenomena using multiple theoretical frameworks",
      type: "project",
      dueWeeks: 10,
      maxScore: 100,
      instructions: `# Communication Theory Analysis Project

## Objective
Apply multiple communication theories to analyze a real-world communication phenomenon, demonstrating understanding of theoretical concepts and their practical applications.

## Requirements
1. **Phenomenon Selection**: Choose a current communication event, campaign, or practice
2. **Theoretical Application**: Apply at least 3 different communication theories from the course
3. **Critical Analysis**: Evaluate the strengths and limitations of each theoretical perspective
4. **Practical Implications**: Discuss how theoretical insights could improve communication practice
5. **Research Integration**: Incorporate relevant scholarly sources and current examples
6. **Presentation**: Prepare both written analysis and oral presentation

## Deliverables
- Theoretical analysis paper (12-15 pages)
- Literature review of relevant communication research
- Case study analysis with theoretical applications
- Presentation slides and oral presentation (15 minutes)
- Practical recommendations based on theoretical insights`,
      submissionFormat: "pdf",
      resources: [
        "Communication theory databases and journals",
        "Contemporary communication case studies",
        "Theoretical framework templates"
      ]
    }
  ],

  finalExam: {
    id: "communication-theory-final",
    title: "Introduction to Communication Theory Final Exam",
    description: "Comprehensive examination covering all communication theory concepts and applications",
    timeLimit: 120,
    passingScore: 85,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "Which communication model best represents the complexity of modern digital communication?",
        options: [
          "Linear model",
          "Interactive model", 
          "Transactional model",
          "Mathematical model"
        ],
        correctAnswer: 2,
        explanation: "The transactional model best represents modern digital communication because it accounts for simultaneous sending and receiving, multiple channels, and the co-creation of meaning that characterizes digital interactions."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "In organizational communication, a 'supportive climate' is characterized by:",
        options: [
          "Strict hierarchy and control",
          "Trust, openness, and participation",
          "Competition between employees",
          "Minimal communication between levels"
        ],
        correctAnswer: 1,
        explanation: "A supportive organizational communication climate is characterized by trust, openness, participation, recognition, and a learning orientation that encourages honest communication and collaboration."
      }
    ]
  },

  quizzes: [
    {
      id: "comprehensive-theory-quiz",
      title: "Comprehensive Communication Theory Quiz",
      description: "Advanced quiz covering all aspects of communication theory",
      timeLimit: 30,
      passingScore: 85,
      attempts: 2,
      questions: [
        {
          id: "comp-q1",
          type: "multiple-choice",
          question: "Which theory best explains how relationships develop through gradual self-disclosure?",
          options: [
            "Social Exchange Theory",
            "Social Penetration Theory",
            "Uncertainty Reduction Theory",
            "Social Cognitive Theory"
          ],
          correctAnswer: 1,
          explanation: "Social Penetration Theory explains how relationships develop through gradual, reciprocal self-disclosure that moves from surface-level to increasingly intimate information sharing."
        },
        {
          id: "comp-q2",
          type: "multiple-choice",
          question: "Digital media's 'affordances' refer to:",
          options: [
            "The cost of using technology",
            "The action possibilities that technology enables or constrains",
            "The speed of digital communication",
            "The popularity of digital platforms"
          ],
          correctAnswer: 1,
          explanation: "Affordances refer to the action possibilities that exist in the relationship between users and technology - what the technology enables or constrains users to do in specific contexts."
        },
        {
          id: "comp-q3",
          type: "multiple-choice",
          question: "According to Media Ecology Theory, 'the medium is the message' means:",
          options: [
            "Content doesn't matter in communication",
            "The characteristics of communication technology are more important than content",
            "Messages should be delivered through multiple media",
            "Medium-length messages are most effective"
          ],
          correctAnswer: 1,
          explanation: "McLuhan's famous phrase suggests that the characteristics and effects of communication technology itself are more significant than any particular content transmitted through that technology."
        }
      ]
    }
  ]
};